import type { FunctionComponent } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { LinkEntity } from "shared/components/common/base-entity-link/types";
import { section } from "shared/data";

import type { sitemapStyleQuery } from "scmp-app/queries/__generated__/sitemapStyleQuery.graphql";

import type { MenuItem } from "./data";
import { menuMoreData, menuOthersData } from "./data";
import {
  Anchor,
  Container,
  MenuItemLabel,
  MenuLinkContainer,
  MenuMoreContainer,
  MenuOthersContainer,
  MenuSectionContainer,
  MenuSectionLinkContainer,
  StyledBaseEntityLink,
} from "./styles";

type MenuData = {
  label: string;
  links: LinkEntity[];
};

type Props = {
  className?: string;
};

export const StyleSitemap: FunctionComponent<Props> = ({ className }) => {
  const data = useLazyLoadQuery<sitemapStyleQuery>(
    graphql`
      query sitemapStyleQuery {
        queue(filter: { name: "scmp_pwa_oneline_menu_style" }) {
          items(first: 6) {
            edges {
              node {
                ... on Section {
                  entityId
                  entityUuid
                  urlAlias
                  name
                }
              }
            }
          }
        }
      }
    `,
    {},
  );

  const getMenuName = (name?: string, entityId?: string) => {
    if (!name || !entityId) return "";
    if (entityId === section.style.entityId) return "LATEST STORIES";
    return name;
  };

  const menuSectionData: MenuData = {
    label: "SEE MORE STORIES",
    links: [
      ...(data.queue?.items?.edges.map(item => ({
        entityId: item.node.entityId ?? "",
        entityUuid: item.node.entityUuid ?? "",
        name: getMenuName(item.node.name, item.node.entityId),
        routeType: "Section",
        urlAlias: item.node.urlAlias ?? "",
      })) ?? []),
    ],
  };

  const renderAnchor = (link: MenuItem, index: number) => (
    <Anchor href={link.url} key={index}>
      {link.name}
    </Anchor>
  );

  return (
    <Container className={className}>
      <MenuSectionContainer>
        <MenuItemLabel>{menuSectionData.label}</MenuItemLabel>
        <MenuSectionLinkContainer>
          {menuSectionData.links.map((item, index) => (
            <StyledBaseEntityLink
              key={index}
              linkEntity={item}
              query={{ appBarVariant: "scmp/magazines-style" }}
            >
              {item.name}
            </StyledBaseEntityLink>
          ))}
        </MenuSectionLinkContainer>
      </MenuSectionContainer>
      <MenuMoreContainer>
        <MenuItemLabel>{menuMoreData.label}</MenuItemLabel>
        <MenuLinkContainer>
          {menuMoreData.links.map((item, index) => renderAnchor(item, index))}
        </MenuLinkContainer>
      </MenuMoreContainer>
      <MenuOthersContainer>
        <MenuItemLabel>{menuOthersData.label}</MenuItemLabel>
        <MenuLinkContainer>
          {menuOthersData.links.map((item, index) => renderAnchor(item, index))}
        </MenuLinkContainer>
      </MenuOthersContainer>
    </Container>
  );
};
